import ChevronRightTwoToneIcon from "@mui/icons-material/ChevronRightTwoTone";
import FindInPageTwoToneIcon from "@mui/icons-material/FindInPageTwoTone";
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone";
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Hidden,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  Slide,
  TextField,
  Theme,
  Tooltip,
  Typography,
  lighten
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { TransitionProps } from "@mui/material/transitions";

import { ChangeEvent, ReactElement, Ref, forwardRef, useState } from "react";

import { getDataSearch } from "@/api/searchfeature/actions";
import CButton, { CIconButton } from "@/components/Button";
import { MODEL_TYPE_SEARCH } from "@/configs";
import { PATH } from "@/configs/router/routerConfig";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement<any, any> },
  ref: Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DialogWrapper = styled(Dialog)(
  () => `
    .MuiDialog-container {
        height: auto;
    }

    .MuiDialog-paperScrollPaper {
        max-height: calc(100vh - 64px)
    }
`
);

const SearchInputWrapper = styled(TextField)(
  ({ theme }) => `
    background: ${theme.colors.alpha.white[100]};

    .MuiInputBase-input {
        font-size: ${theme.typography.pxToRem(17)};
    }
`
);

const DialogTitleWrapper = styled(DialogTitle)(
  ({ theme }) => `
    background: ${theme.colors.alpha.black[5]};
    padding: ${theme.spacing(3)}
`
);

function HeaderSearch() {
  const { t } = useTranslation();
  const [openSearchResults, setOpenSearchResults] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const router = useRouter();

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(event.target.value);

    if (event.target.value) {
      if (!openSearchResults) {
        setOpenSearchResults(true);
      }
    } else {
      setOpenSearchResults(false);
    }
  };

  const handleSearch = () => {
    const dataSearch = `{"input":"${searchValue}"}`;

    getDataSearch({}, dataSearch).then((res: any) => {
      setSearchResult(res?.content.data || []);
    });
  };

  const handleSearchData = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch(); // Call search function on Enter key
    }
  };

  const handleBlur = () => {
    handleSearch(); // Call search function on input blur
  };

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const getUrlDetail = (type: any, id: any) => {
    switch (type) {
      case MODEL_TYPE_SEARCH.SALES_CUSTOMER:
        return PATH.CUSTOMER + "/" + id;
      case MODEL_TYPE_SEARCH.SALES_ACCOUNT:
        return `/groups/${id}`;
      case MODEL_TYPE_SEARCH.SALES_ACTIVITY:
        return `/activities/${id}`;
      case MODEL_TYPE_SEARCH.SALES_CAMPAIGN:
        return PATH.CAMPAIGN + "/" + id;
      case MODEL_TYPE_SEARCH.SALES_AGENT:
        return PATH.AGENTS_LIST + "/" + id;
      case MODEL_TYPE_SEARCH.SALES_PRODUCT:
        return PATH.PRODUCT + "/" + id;
      case MODEL_TYPE_SEARCH.SALES_MEMBER:
        return `/members/${id}`;
      case MODEL_TYPE_SEARCH.SALES_ESCALATION:
        return PATH.SALES_ESCALATION + "/" + id;
      case MODEL_TYPE_SEARCH.TASKS:
        return PATH.AI_TASK + "/" + id;
      case MODEL_TYPE_SEARCH.PERMISSION:
        return PATH.SYSTEM_PERMISSIONS;
      case MODEL_TYPE_SEARCH.GROUP:
        return PATH.SYSTEM_GROUPS_DETAIL.replace("[id]", String(id));
      case MODEL_TYPE_SEARCH.APP_INTEGRATIONS:
        return PATH.TOOLS_TEMPLATE + "/" + id;
      case MODEL_TYPE_SEARCH.APP_PAGES:
        return ""; // ai-apps removed
      case MODEL_TYPE_SEARCH.PROMPT:
        return PATH.TOOLS_PROMPTS + "/" + id;
      case MODEL_TYPE_SEARCH.CHAIN:
        return PATH.TOOLS_CHAINS + "/" + id;
      case MODEL_TYPE_SEARCH.TOPIC:
        return PATH.TOOLS_TOPICS + "/" + id;
      default:
        return "";
    }
  };

  const getTitleDetail = (type: any) => {
    switch (type) {
      case MODEL_TYPE_SEARCH.SALES_CUSTOMER:
        return "Sales Customer";
      case MODEL_TYPE_SEARCH.SALES_ACCOUNT:
        return "Sales Account";
      case MODEL_TYPE_SEARCH.SALES_ACTIVITY:
        return `Sales Activity`;
      case MODEL_TYPE_SEARCH.SALES_CAMPAIGN:
        return "Sales Campaign";
      case MODEL_TYPE_SEARCH.SALES_AGENT:
        return "Sales Agent";
      case MODEL_TYPE_SEARCH.SALES_PRODUCT:
        return "Sales Product";
      case MODEL_TYPE_SEARCH.SALES_MEMBER:
        return "Sales Member";
      case MODEL_TYPE_SEARCH.SALES_ESCALATION:
        return "Sales Escalation";
      case MODEL_TYPE_SEARCH.TASKS:
        return "Sales Tasks";
      case MODEL_TYPE_SEARCH.PERMISSION:
        return "System Permissions";
      case MODEL_TYPE_SEARCH.GROUP:
        return "System Groups";
      case MODEL_TYPE_SEARCH.APP_INTEGRATIONS:
        return "App Integrations";
      case MODEL_TYPE_SEARCH.APP_PAGES:
        return "App Pages";
      case MODEL_TYPE_SEARCH.PROMPT:
        return "Prompt";
      case MODEL_TYPE_SEARCH.CHAIN:
        return "Chain";
      case MODEL_TYPE_SEARCH.TOPIC:
        return "Topic";
      default:
        return "";
    }
  };

  return (
    <>
      <Tooltip arrow title={t("Search")}>
        <CIconButton onClick={handleClickOpen} aria-label={t("Open search")}>
          <SearchTwoToneIcon />
        </CIconButton>
      </Tooltip>

      <DialogWrapper
        open={open}
        TransitionComponent={Transition}
        maxWidth="md"
        fullWidth
        scroll="paper"
        onClose={handleClose}
      >
        <DialogTitleWrapper>
          <SearchInputWrapper
            value={searchValue}
            autoFocus={true}
            onChange={handleSearchChange}
            onKeyDown={handleSearchData}
            onBlur={handleBlur}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchTwoToneIcon />
                </InputAdornment>
              )
            }}
            placeholder={t("Search terms here...")}
            fullWidth
            label={t("Search")}
          />
        </DialogTitleWrapper>
        <Divider />

        {openSearchResults && (
          <DialogContent>
            <Box sx={{ pt: 0, pb: 1 }} display="flex" justifyContent="space-between">
              <Typography variant="body2" component="span">
                {t("Search results for")}{" "}
                <Typography sx={{ fontWeight: "bold" }} variant="body1" component="span">
                  {searchValue}
                </Typography>
              </Typography>
              <Link href="#" variant="body2" underline="hover">
                {t("Advanced search")}
              </Link>
            </Box>
            <Divider sx={{ my: 1 }} />
            <List disablePadding>
              {searchResult.map((item: any, index: number) => (
                <>
                  <ListItem key={index} onClick={() => router.push(getUrlDetail(item.model_type, item.id))}>
                    <Hidden>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            background: (theme: Theme) => theme.palette.secondary.main
                          }}
                        >
                          <FindInPageTwoToneIcon />
                        </Avatar>
                      </ListItemAvatar>
                    </Hidden>
                    <Box flex="1">
                      <Box display="flex" justifyContent="space-between">
                        <Link href="#" underline="hover" sx={{ fontWeight: "bold" }} variant="body2">
                          {item.name ? item.name : getTitleDetail(item.type)}
                        </Link>
                      </Box>
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          color: (theme: Theme) => lighten(theme.palette.secondary.main, 0.5)
                        }}
                      >
                        {item.model_type}
                      </Typography>
                    </Box>
                    <ChevronRightTwoToneIcon />
                  </ListItem>
                  {searchResult.length - 1 !== index && <Divider sx={{ my: 1 }} component="li" />}
                </>
              ))}

              {/* <ListItem button>
                <Hidden smDown>
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        background: (theme: Theme) => theme.palette.secondary.main
                      }}
                    >
                      <FindInPageTwoToneIcon />
                    </Avatar>
                  </ListItemAvatar>
                </Hidden>
                <Box flex="1">
                  <Box display="flex" justifyContent="space-between">
                    <Link href="#" underline="hover" sx={{ fontWeight: "bold" }} variant="body2">
                      Example Projects Application
                    </Link>
                  </Box>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: (theme: Theme) => lighten(theme.palette.secondary.main, 0.5)
                    }}
                  >
                    This is yet another search result pointing to a app page.
                  </Typography>
                </Box>
                <ChevronRightTwoToneIcon />
              </ListItem>
              <Divider sx={{ my: 1 }} component="li" />
              <ListItem component={"button"}>
                <Hidden smDown>
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        background: (theme: Theme) => theme.palette.secondary.main
                      }}
                    >
                      <FindInPageTwoToneIcon />
                    </Avatar>
                  </ListItemAvatar>
                </Hidden>
                <Box flex="1">
                  <Box display="flex" justifyContent="space-between">
                    <Link href="#" underline="hover" sx={{ fontWeight: "bold" }} variant="body2">
                      Search Results Page
                    </Link>
                  </Box>
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: (theme: Theme) => lighten(theme.palette.secondary.main, 0.5)
                    }}
                  >
                    Choose if you would like to show or not this typography section here...
                  </Typography>
                </Box>
                <ChevronRightTwoToneIcon />
              </ListItem> */}
            </List>
            <Divider sx={{ mt: 1, mb: 2 }} />
            <Box sx={{ textAlign: "center" }}>
              <CButton color="primary">{t("View all search results")}</CButton>
            </Box>
          </DialogContent>
        )}
      </DialogWrapper>
    </>
  );
}

export default HeaderSearch;
